// EXPEDIENTES (2026-08-13) — acervo de ofícios, declarações e memorandos.
//
// SIGILO NA TELA: todo mundo vê que OF-2026-004 existe (número, tipo, data).
// Quem não participa NÃO vê assunto, destinatário nem quem escreveu — o
// backend devolve esses campos como null, e a linha aparece com "🔒 Sigiloso"
// e não abre. Não tente "melhorar" mostrando o assunto em tooltip: a linha
// cega é o produto, não uma limitação.
//
// A busca por texto só encontra assunto/destinatário nos documentos em que a
// pessoa participa (regra do backend). Por código, encontra qualquer um — é
// dado público. Sem isso o filtro viraria oráculo do assunto alheio.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  App as AntApp, Button, Card, Empty, Flex, Input, Select, Space, Spin,
  Table, Tag, Tooltip, Typography, Checkbox,
} from 'antd';
import { PlusOutlined, ReloadOutlined, LockOutlined, FilePdfOutlined } from '@ant-design/icons';
import { fmtDataBr } from '../utils/dataFmt';
import {
  listarExpedientes, listarTipos, pdfExpediente,
  ExpedienteLinha, TipoExpediente, STATUS_CFG,
} from './administrativo-expedientes/api';
import ModalNovoExpediente from './administrativo-expedientes/ModalNovoExpediente';
import DrawerExpediente from './administrativo-expedientes/DrawerExpediente';

const { Text, Title } = Typography;

export default function AdministrativoExpedientes() {
  const { message } = AntApp.useApp();
  const nav = useNavigate();

  const [linhas, setLinhas] = useState<ExpedienteLinha[]>([]);
  const [tipos, setTipos] = useState<TipoExpediente[]>([]);
  const [loading, setLoading] = useState(true);
  const [verTodos, setVerTodos] = useState(false);

  const [fAno, setFAno] = useState<number | null>(new Date().getFullYear());
  const [fTipo, setFTipo] = useState<number | null>(null);
  const [fStatus, setFStatus] = useState<string | null>(null);
  const [soMeus, setSoMeus] = useState(false);
  const [busca, setBusca] = useState('');

  const [modalNovo, setModalNovo] = useState(false);
  const [detalheId, setDetalheId] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const d = await listarExpedientes({
        ano: fAno, tipo_id: fTipo, status: fStatus,
        so_meus: soMeus, busca: busca.trim() || undefined,
      });
      setLinhas(d.expedientes || []);
      setVerTodos(!!d.ver_todos);
    } catch (e: any) {
      message.error('Falha ao carregar: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }, [fAno, fTipo, fStatus, soMeus, busca, message]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    listarTipos().then(d => setTipos(d.tipos || [])).catch(() => setTipos([]));
  }, []);

  const anos = useMemo(() => {
    const atual = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => atual - i);
  }, []);

  async function baixarPdf(id: string) {
    try {
      const r = await pdfExpediente(id);
      window.open(r.url, '_blank');
    } catch (e: any) {
      message.error('Falha ao gerar o PDF: ' + (e.response?.data?.error || e.message));
    }
  }

  function abrir(l: ExpedienteLinha) {
    if (!l.participo) return;                    // linha cega não abre
    if (l.status === 'rascunho') nav(`/administrativo/expedientes/${l.id}`);
    else setDetalheId(l.id);
  }

  const colunas = [
    {
      title: 'Código', dataIndex: 'codigo', width: 130,
      render: (v: string | null, l: ExpedienteLinha) =>
        v ? <Text strong style={{ fontFamily: 'monospace' }}>{v}</Text>
          : <Tag>rascunho</Tag>,
    },
    {
      title: 'Tipo', dataIndex: 'tipo_nome', width: 150,
      render: (v: string, l: ExpedienteLinha) => (
        <Space size={4}>
          <Text>{v}</Text>
          {l.escopo === 'interno' && <Tag color="blue" style={{ fontSize: 10 }}>interno</Tag>}
        </Space>
      ),
    },
    {
      title: 'Data', dataIndex: 'data_documento', width: 105,
      render: (v: string | null) => v ? fmtDataBr(v) : '—',
    },
    {
      title: 'Assunto', dataIndex: 'assunto',
      render: (v: string | null, l: ExpedienteLinha) => l.participo
        ? <Text>{v || <Text type="secondary">— sem assunto —</Text>}</Text>
        : (
          <Tooltip title="Você não participa deste expediente. O acervo mostra que ele existe; o conteúdo é restrito a quem está envolvido.">
            <Text type="secondary" italic><LockOutlined /> Sigiloso</Text>
          </Tooltip>
        ),
    },
    {
      title: 'Destinatário', dataIndex: 'destinatario_org', width: 200,
      render: (v: string | null, l: ExpedienteLinha) => !l.participo
        ? <Text type="secondary">—</Text>
        : <Text>{v || l.destinatario_nome || '—'}</Text>,
    },
    {
      title: 'Situação', dataIndex: 'status', width: 110,
      render: (v: string, l: ExpedienteLinha) => (
        <Space direction="vertical" size={0}>
          <Tag color={STATUS_CFG[v]?.cor}>{STATUS_CFG[v]?.label || v}</Tag>
          {l.participo && l.status === 'cancelado' && l.cancelado_motivo && (
            <Tooltip title={l.cancelado_motivo}>
              <Text type="secondary" style={{ fontSize: 10 }}>motivo ▾</Text>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: '', width: 60,
      render: (_: any, l: ExpedienteLinha) => l.participo && l.status !== 'rascunho' ? (
        <Tooltip title="Baixar PDF">
          <Button type="text" size="small" icon={<FilePdfOutlined />}
            onClick={(e) => { e.stopPropagation(); baixarPdf(l.id); }} />
        </Tooltip>
      ) : null,
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 12 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>📄 Expedientes</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Ofícios, declarações e comunicados internos, numerados e arquivados.
            {verTodos && ' Você enxerga o conteúdo de todos — cada abertura fica registrada.'}
          </Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={carregar} />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalNovo(true)}>
            Novo expediente
          </Button>
        </Space>
      </Flex>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Flex wrap gap={8} align="center">
          <Select placeholder="Ano" allowClear style={{ width: 100 }} value={fAno}
            onChange={setFAno} options={anos.map(a => ({ value: a, label: String(a) }))} />
          <Select placeholder="Tipo" allowClear style={{ width: 190 }} value={fTipo}
            onChange={setFTipo}
            options={tipos.map(t => ({ value: t.id, label: `${t.prefixo} — ${t.nome}` }))} />
          <Select placeholder="Situação" allowClear style={{ width: 140 }} value={fStatus}
            onChange={setFStatus}
            options={Object.entries(STATUS_CFG).map(([k, v]) => ({ value: k, label: v.label }))} />
          <Input.Search placeholder="Buscar por código, assunto ou destinatário"
            allowClear style={{ width: 320 }} onSearch={setBusca}
            onChange={(e) => { if (!e.target.value) setBusca(''); }} />
          <Checkbox checked={soMeus} onChange={(e) => setSoMeus(e.target.checked)}>
            Só os meus
          </Checkbox>
        </Flex>
      </Card>

      <Spin spinning={loading}>
        {!loading && !linhas.length ? (
          <Empty description="Nenhum expediente com esses filtros" />
        ) : (
          <Table
            rowKey="id"
            dataSource={linhas}
            columns={colunas as any}
            size="small"
            pagination={{ pageSize: 25, showSizeChanger: true }}
            scroll={{ x: 1000 }}
            onRow={(l) => ({
              onClick: () => abrir(l),
              style: { cursor: l.participo ? 'pointer' : 'default',
                       opacity: l.participo ? 1 : 0.62 },
            })}
          />
        )}
      </Spin>

      <ModalNovoExpediente
        open={modalNovo} tipos={tipos}
        onClose={() => setModalNovo(false)}
        onCriado={(id) => { setModalNovo(false); nav(`/administrativo/expedientes/${id}`); }}
      />

      <DrawerExpediente
        id={detalheId}
        onClose={() => setDetalheId(null)}
        onMudou={carregar}
      />
    </div>
  );
}
